import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, Award, Calendar, Plus, Check, X, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { UserProfile } from '../types';
import SEO from './SEO';
import { loadStoredProgress, ProgressEntry } from '../utils/progressHistory';

interface ProgressDashboardProps {
  userProfile: UserProfile | null;
  onProfileUpdate: (profile: UserProfile) => Promise<boolean> | void;
}

const ProgressDashboard: React.FC<ProgressDashboardProps> = ({ userProfile, onProfileUpdate }) => {
  const [progressHistory, setProgressHistory] = useState<ProgressEntry[]>([]);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [newEntry, setNewEntry] = useState<ProgressEntry>({
    date: new Date().toISOString().split('T')[0],
    handicap: userProfile?.handicap,
    driverDistance: userProfile?.golfBag?.find(c => c.type === 'driver')?.distance,
    sevenIronDistance: userProfile?.golfBag?.find(c => c.type === '7-iron')?.distance,
    notes: ''
  });

  // Load progress history from local storage
  useEffect(() => {
    // Try to load from localStorage
    const savedHistory = localStorage.getItem(`clubiq-progress-${userProfile?.id}`);
    setProgressHistory(loadStoredProgress(savedHistory));
  }, [userProfile]);

  const handleSaveEntry = () => {
    const updatedHistory = [...progressHistory, newEntry].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    setProgressHistory(updatedHistory);
    
    // Save to profile
    if (userProfile) {
      // Save to localStorage
      localStorage.setItem(`clubiq-progress-${userProfile.id}`, JSON.stringify(updatedHistory));
    }
    
    setShowAddEntry(false);
    setNewEntry({
      date: new Date().toISOString().split('T')[0],
      handicap: userProfile?.handicap,
      driverDistance: undefined,
      sevenIronDistance: undefined,
      notes: ''
    });
  };

  // Calculate improvements
  const calculateImprovement = (field: 'handicap' | 'driverDistance' | 'sevenIronDistance') => {
    const validEntries = progressHistory.filter(e => e[field] !== undefined);
    if (validEntries.length < 2) return null;
    
    const oldest = validEntries[0][field] as number;
    const newest = validEntries[validEntries.length - 1][field] as number;
    const diff = newest - oldest;
    
    // For handicap, negative is good. For distance, positive is good.
    const isImprovement = field === 'handicap' ? diff < 0 : diff > 0;
    
    return {
      value: Math.abs(diff),
      isImprovement,
      percentChange: ((diff / oldest) * 100).toFixed(1)
    };
  };

  const handicapImprovement = calculateImprovement('handicap');
  const driverImprovement = calculateImprovement('driverDistance');
  const ironImprovement = calculateImprovement('sevenIronDistance');

  if (!userProfile) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sign In to Track Progress</h2>
          <p className="text-gray-600">Create an account to start tracking your golf improvement journey.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Progress Dashboard"
        description="Track your golf improvement over time. Monitor handicap changes, distance gains, and see your journey visualized."
        url="/progress"
      />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                <TrendingUp className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Progress Dashboard</h1>
                <p className="text-teal-100">Track your improvement over time</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddEntry(true)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Log Progress
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Handicap */}
          <div className="bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Handicap</span>
              </div>
              {handicapImprovement && (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  handicapImprovement.isImprovement 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {handicapImprovement.isImprovement ? '↓' : '↑'} {handicapImprovement.value.toFixed(1)}
                </span>
              )}
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {userProfile.handicap ?? '--'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {handicapImprovement 
                ? `${Math.abs(parseFloat(handicapImprovement.percentChange))}% ${handicapImprovement.isImprovement ? 'improvement' : 'increase'}`
                : 'Track over time'}
            </div>
          </div>

          {/* Driver Distance */}
          <div className="bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-600">Driver</span>
              </div>
              {driverImprovement && (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  driverImprovement.isImprovement 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {driverImprovement.isImprovement ? '↑' : '↓'} {driverImprovement.value} yds
                </span>
              )}
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {userProfile.golfBag?.find(c => c.type === 'driver')?.distance ?? '--'}
              <span className="text-lg text-gray-500 ml-1">yds</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {driverImprovement ? `${driverImprovement.percentChange}% change` : 'Carry distance'}
            </div>
          </div>

          {/* 7-Iron Distance */}
          <div className="bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-medium text-gray-600">7-Iron</span>
              </div>
              {ironImprovement && (
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  ironImprovement.isImprovement 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {ironImprovement.isImprovement ? '↑' : '↓'} {ironImprovement.value} yds
                </span>
              )}
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {userProfile.golfBag?.find(c => c.type === '7-iron')?.distance ?? '--'}
              <span className="text-lg text-gray-500 ml-1">yds</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {ironImprovement ? `${ironImprovement.percentChange}% change` : 'Carry distance'}
            </div>
          </div>
        </div>

        {/* Charts */}
        {progressHistory.length > 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Handicap Chart */}
            {progressHistory.some(e => e.handicap !== undefined) && (
              <div className="bg-white rounded-xl shadow-lg p-5">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  Handicap Trend
                </h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={progressHistory.filter(e => e.handicap !== undefined)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short' })}
                        stroke="#9ca3af"
                        fontSize={12}
                      />
                      <YAxis stroke="#9ca3af" fontSize={12} reversed />
                      <Tooltip 
                        formatter={(value: number) => [value.toFixed(1), 'Handicap']}
                        labelFormatter={(label) => new Date(label).toLocaleDateString()}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="handicap" 
                        stroke="#3b82f6" 
                        fill="#93c5fd"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Distance Chart */}
            {progressHistory.some(e => e.driverDistance !== undefined || e.sevenIronDistance !== undefined) && (
              <div className="bg-white rounded-xl shadow-lg p-5">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Distance Trend
                </h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={progressHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short' })}
                        stroke="#9ca3af"
                        fontSize={12}
                      />
                      <YAxis stroke="#9ca3af" fontSize={12} />
                      <Tooltip 
                        formatter={(value: number, name: string) => [
                          `${value} yds`, 
                          name === 'driverDistance' ? 'Driver' : '7-Iron'
                        ]}
                        labelFormatter={(label) => new Date(label).toLocaleDateString()}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="driverDistance" 
                        stroke="#8b5cf6" 
                        strokeWidth={2}
                        dot={{ fill: '#8b5cf6', r: 4 }}
                        name="driverDistance"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="sevenIronDistance" 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        dot={{ fill: '#f59e0b', r: 4 }}
                        name="sevenIronDistance"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-3 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span className="text-gray-600">Driver</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span className="text-gray-600">7-Iron</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Progress History */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-5 py-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-600" />
              Progress History
            </h3>
          </div>
          {progressHistory.length === 0 ? (
            <div className="p-8 text-center">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No progress entries yet. Start logging your improvement!</p>
            </div>
          ) : (
            <div className="divide-y max-h-64 overflow-y-auto">
              {[...progressHistory].reverse().map((entry, idx) => (
                <div key={idx} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">
                      {new Date(entry.date).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </div>
                    {entry.notes && (
                      <div className="text-sm text-gray-500">{entry.notes}</div>
                    )}
                  </div>
                  <div className="flex gap-4 text-sm">
                    {entry.handicap !== undefined && (
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{entry.handicap}</div>
                        <div className="text-xs text-gray-500">HCP</div>
                      </div>
                    )}
                    {entry.driverDistance !== undefined && (
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{entry.driverDistance}</div>
                        <div className="text-xs text-gray-500">Driver</div>
                      </div>
                    )}
                    {entry.sevenIronDistance !== undefined && (
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{entry.sevenIronDistance}</div>
                        <div className="text-xs text-gray-500">7I</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Entry Modal */}
        {showAddEntry && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Log Progress</h3>
                <button 
                  onClick={() => setShowAddEntry(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={newEntry.date}
                    onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Handicap</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newEntry.handicap ?? ''}
                    onChange={(e) => setNewEntry({ ...newEntry, handicap: e.target.value ? parseFloat(e.target.value) : undefined })}
                    placeholder="Enter handicap"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Driver (yds)</label>
                    <input
                      type="number"
                      value={newEntry.driverDistance ?? ''}
                      onChange={(e) => setNewEntry({ ...newEntry, driverDistance: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="e.g. 250"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">7-Iron (yds)</label>
                    <input
                      type="number"
                      value={newEntry.sevenIronDistance ?? ''}
                      onChange={(e) => setNewEntry({ ...newEntry, sevenIronDistance: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="e.g. 155"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                  <textarea
                    value={newEntry.notes}
                    onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                    placeholder="Any notes about this session..."
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddEntry(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEntry}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center justify-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  Save Entry
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProgressDashboard;

